import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { ApiError, useApiClient } from "../../../apiClient";
import { translate } from "../../../i18n";
import {
    AdminGuestPlayerResponseDto,
    CreateAdminGuestPlayerDto,
} from "../../../models/user/dto/admin-guest-player.dto";

/**
 * MYS-140 — finds (by phone) or creates a guest player in the organization so
 * the admin-booking modal can send a payment link to someone who is not in the
 * database. The API answers 400 for an input it refuses and 409 for a number
 * that belongs to an archived player; both messages are written for the
 * operator, so they are shown as they come.
 *
 * There is deliberately no success callback here: the caller passes it to
 * `mutate(body, { onSuccess })`. React Query runs a hook-level `onSuccess`
 * even after the form unmounted, so a reply that lands after the operator
 * closed the modal used to re-select that guest — and the email typed for
 * them — on the NEXT booking. A `mutate`-level callback is dropped once the
 * calling component is gone.
 */
export const useCreateGuestPlayer = (organizationId: string) => {
    const apiClient = useApiClient();
    const toast = useToast();
    return useMutation({
        mutationKey: ['createGuestPlayer', organizationId],
        mutationFn: (body: CreateAdminGuestPlayerDto) =>
            apiClient.fetchApi<AdminGuestPlayerResponseDto, CreateAdminGuestPlayerDto>({
                method: 'POST',
                endpoint: `users/admin/organizations/${organizationId}/guest-players`,
                body,
            }),
        onError: (err) => {
            if (err instanceof ApiError && (err.status === 400 || err.status === 409)) {
                toast({
                    title: "Impossible d'ajouter ce joueur",
                    description: err.message,
                    status: 'warning',
                    duration: 6000,
                    position: 'top-right',
                    isClosable: true,
                });
                return;
            }
            toast({
                title: translate('error'),
                status: 'error',
                duration: 3000,
                position: 'top-right',
            });
        },
    });
};
